import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Upload, UploadFile, message } from 'antd';
import { FileImageOutlined } from '@ant-design/icons';
import ImgCrop from 'antd-img-crop';
import { useTranslation } from 'react-i18next';

import * as S from './styles';

interface ImageUploadProps {
  onChange?: (file: any) => void;
  dimensions?: {
    width: number;
    height: number;
  };
  defaultFile?: UploadFile<any>;
  allowCut?: boolean;
  limitSize?: number;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  onChange,
  dimensions,
  defaultFile,
  allowCut,
  limitSize = 2,
}) => {
  const { t } = useTranslation('main');
  const [fileList, setFileList] = useState<UploadFile<any>[]>([]);

  useEffect(() => {
    if (defaultFile) {
      setFileList([defaultFile]);
    } else {
      setFileList([]);
    }
  }, [defaultFile]);

  const handleOnUpload = useCallback(
    (file: File) => {
      const isOnTheLimit = file.size / 1024 / 1024 < limitSize;
      if (!isOnTheLimit) {
        message.error(
          t('image-upload.max-upload-size-error', { value: limitSize }),
        );
      } else {
        onChange?.(file);
        setFileList([
          {
            uid: file.name,
            name: file.name,
            status: 'done',
            size: file.size,
            type: file.type,
            url: URL.createObjectURL(file),
          },
        ]);
      }
      return false;
    },
    [limitSize, onChange, t],
  );

  const handleOnChange = useCallback(
    ({ file }: { file: UploadFile<any> }) => {
      const wasRemoved = file?.status === 'removed';
      if (wasRemoved) {
        onChange?.(undefined);
        setFileList([]);
      }
    },
    [onChange],
  );

  const UploadEl = useMemo(
    () => (
      <Upload
        maxCount={1}
        accept=".jpg, .png"
        listType="picture"
        fileList={fileList}
        onChange={handleOnChange}
        beforeUpload={handleOnUpload}
        className="image-upload"
      >
        {fileList.length < 1 && (
          <div className="image-upload-content">
            <FileImageOutlined className="image-upload-icon" />
            <div>
              <p className="image-upload-title">
                {t('image-upload.click-or-drag')}
              </p>
              <p className="image-upload-subtitle">
                {t('image-upload.support')}
                {dimensions &&
                  t('image-upload.dimensions', {
                    value: `${dimensions?.width}x${dimensions?.height}px`,
                  })}
                &nbsp;
                {!!limitSize &&
                  t('image-upload.max-upload-size', { value: limitSize })}
              </p>
            </div>
          </div>
        )}
      </Upload>
    ),
    [dimensions, fileList, handleOnChange, handleOnUpload, limitSize, t],
  );

  return (
    <S.ImgUploadContainer>
      {allowCut ? (
        <ImgCrop
          quality={1}
          modalTitle={t('image-upload.crop-modal-title')}
          aspect={dimensions && dimensions?.width / dimensions?.height}
        >
          {UploadEl}
        </ImgCrop>
      ) : (
        UploadEl
      )}
    </S.ImgUploadContainer>
  );
};

export default ImageUpload;
